import React, { useState, useEffect, useRef } from 'react';
import ContentEditable from 'react-contenteditable';
import useGenerateText from '../../hooks/useGenerateText';
import { stripAllFormatting } from '../../utils/textUtils';

interface TextOptionVariations {
  original: string;
  tuSingular: string;
  tuPlural: string;
  udSingular: string;
  udPlural: string;
}

interface TextSelectableEditorProps {
  content: string[] | { textOptions: TextOptionVariations[] };
  onChange: (content: { textOptions: TextOptionVariations[] }) => void;
  isEditable: boolean;
  onEditableChange: (editable: boolean) => void;
  allowAddingOptions?: boolean; // New prop to control adding options
  showEditableToggle?: boolean;
}

type TabKey = keyof TextOptionVariations;

const TextSelectableEditor: React.FC<TextSelectableEditorProps> = ({ content, onChange, isEditable, onEditableChange, allowAddingOptions = true, showEditableToggle = true }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('original');
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newOptionText, setNewOptionText] = useState('');
  const [tempChanges, setTempChanges] = useState<Record<TabKey, string>>({} as Record<TabKey, string>);
  const { generateText, isGenerating, error, clearError } = useGenerateText();
  
  // Utility function to fill empty variations with original content
  const fillEmptyVariationsWithOriginal = (variations: TextOptionVariations): TextOptionVariations => {
    const { original, tuSingular, tuPlural, udSingular, udPlural } = variations;
    
    // Check if variations are empty (all blank or just whitespace)
    const isEmpty = (text: string) => !text || text.trim() === '';
    const allVariationsEmpty = isEmpty(tuSingular) && isEmpty(tuPlural) && 
                               isEmpty(udSingular) && isEmpty(udPlural);
    
    // If original has content but variations are empty, fill them
    if (!isEmpty(original) && allVariationsEmpty) {
      return {
        ...variations,
        tuSingular: original,
        tuPlural: original,
        udSingular: original,
        udPlural: original
      };
    }
    
    return variations;
  };
  
  // Initialize options from content prop
  const [textOptions, setTextOptions] = useState<TextOptionVariations[]>(() => {
    let initialOptions: TextOptionVariations[] = [];
    
    if (Array.isArray(content)) {
      // Legacy format - convert string[] to new format
      initialOptions = content.map(text => {
        let textValue = '';
        
        if (typeof text === 'string') {
          textValue = text;
        } else if (text && typeof text === 'object') {
          const textObj = text as any;
          // Check for nested content structures
          if (textObj.content) {
            if (typeof textObj.content === 'string') {
              textValue = textObj.content;
            } else if (textObj.content.text) {
              textValue = textObj.content.text;
            } else if (textObj.content.content && typeof textObj.content.content === 'string') {
              textValue = textObj.content.content;
            }
          }
          
          // Check for other common field names
          if (!textValue) {
            textValue = textObj.text || textObj.textContent || textObj.value || textObj.name || textObj.title || '';
          }
          
          // If still no text found, try JSON stringify as last resort
          if (!textValue) {
            try {
              const stringContent = JSON.stringify(text);
              textValue = stringContent;
            } catch {
              textValue = '';
            }
          }
        } else {
          textValue = (text as any)?.toString() || '';
        }
        
        return {
          original: textValue,
          tuSingular: '',
          tuPlural: '',
          udSingular: '',
          udPlural: ''
        };
      });
    } else if (content && typeof content === 'object' && content.textOptions) {
      
      // Check if textOptions contains variation objects or legacy strings
      if (Array.isArray(content.textOptions)) {
        initialOptions = content.textOptions.map((option: any) => {
          if (typeof option === 'string') {
            // Legacy format - string directly
            return {
              original: option,
              tuSingular: '',
              tuPlural: '',
              udSingular: '',
              udPlural: ''
            };
          } else if (option && typeof option === 'object' && option.original !== undefined) {
            // New format with variations
            return {
              original: option.original || '',
              tuSingular: option.tuSingular || '',
              tuPlural: option.tuPlural || '',
              udSingular: option.udSingular || '',
              udPlural: option.udPlural || ''
            };
          } else {
            // Other legacy formats
            let textValue = '';
            
            if (option && typeof option === 'object') {
              const optionObj = option as any;
              textValue = optionObj.text || optionObj.content || optionObj.value || optionObj.name || optionObj.title || '';
              
              if (!textValue) {
                try {
                  const stringContent = JSON.stringify(option);
                  textValue = stringContent;
                } catch {
                  textValue = '';
                }
              }
            } else {
              textValue = (option as any)?.toString() || '';
            }
            
            return {
              original: textValue,
              tuSingular: '',
              tuPlural: '',
              udSingular: '',
              udPlural: ''
            };
          }
        });
      } else {
        initialOptions = [];
      }
    } else if (content && typeof content === 'object') {
      // Try to extract text options from other legacy formats
      const contentObj = content as any;
      let textOptionsArray: any[] = [];
      
      // Check for nested structure
      if (contentObj.content && Array.isArray(contentObj.content)) {
        textOptionsArray = contentObj.content;
      } else if (contentObj.options && Array.isArray(contentObj.options)) {
        textOptionsArray = contentObj.options;
      } else if (contentObj.items && Array.isArray(contentObj.items)) {
        textOptionsArray = contentObj.items;
      } else {
        // Try to find any array property
        for (const key of Object.keys(contentObj)) {
          if (Array.isArray(contentObj[key])) {
            textOptionsArray = contentObj[key];
            break;
          }
        }
      }
      
      initialOptions = textOptionsArray.map(item => {
        let textValue = '';
        
        if (typeof item === 'string') {
          textValue = item;
        } else if (item && typeof item === 'object') {
          textValue = item.text || item.content || item.value || item.name || item.title || '';
          
          // If still no text found, try JSON stringify as last resort
          if (!textValue) {
            try {
              const stringContent = JSON.stringify(item);
              textValue = stringContent;
            } catch {
              textValue = '';
            }
          }
        } else {
          textValue = (item as any)?.toString() || '';
        }
        
        return {
          original: textValue,
          tuSingular: '',
          tuPlural: '',
          udSingular: '',
          udPlural: ''
        };
      });
    } else {
      // Default empty state
      initialOptions = [];
    }
    
    // Auto-fill empty variations with original content if applicable
    return initialOptions.map(option => fillEmptyVariationsWithOriginal(option));
  });

  // Update parent whenever options change (only call onChange when options actually change)
  const prevOptionsRef = useRef<{textOptions: TextOptionVariations[]} | null>(null);

  useEffect(() => {
    const currentContent = { textOptions };
    
    // Only call onChange if options actually changed
    if (!prevOptionsRef.current ||
        JSON.stringify(prevOptionsRef.current.textOptions) !== JSON.stringify(textOptions)) {
      onChange(currentContent);
      prevOptionsRef.current = { ...currentContent };
    }
  }, [textOptions]);

  const tabs = [
    { key: 'original' as TabKey, label: 'Original' },
    { key: 'tuSingular' as TabKey, label: 'TÚ SINGULAR' },
    { key: 'tuPlural' as TabKey, label: 'TÚ PLURAL' },
    { key: 'udSingular' as TabKey, label: 'UD. SINGULAR' },
    { key: 'udPlural' as TabKey, label: 'UD. PLURAL' }
  ];

  const applyFormatting = (command: string, value?: string) => {
    document.execCommand(command, false, value);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };


  const startAddingOption = () => {
    setIsAddingNew(true);
    setEditingOptionIndex(null);
    setActiveTab('original');
    setNewOptionText('');
    setTempChanges({} as Record<TabKey, string>); // Clear any previous temp changes
    clearError();
  };

  const startEditingOption = (index: number) => {
    setEditingOptionIndex(index);
    setIsAddingNew(false);
    setActiveTab('original');
    setNewOptionText(textOptions[index].original);
    setTempChanges({} as Record<TabKey, string>); // Clear any previous temp changes
    clearError();
  };

  const saveOption = () => {
    // Save current tab's text to tempChanges first
    if (newOptionText.trim() && (editingOptionIndex !== null || isAddingNew)) {
      setTempChanges(prev => ({
        ...prev,
        [activeTab]: newOptionText
      }));
    }

    // Create the final changes object including current text
    const finalChanges = {
      ...tempChanges,
      [activeTab]: newOptionText
    };

    if (isAddingNew) {
      // Adding new option - merge all tempChanges into new option
      const newOptionVariations: TextOptionVariations = {
        original: finalChanges.original || '',
        tuSingular: finalChanges.tuSingular || '',
        tuPlural: finalChanges.tuPlural || '',
        udSingular: finalChanges.udSingular || '',
        udPlural: finalChanges.udPlural || ''
      };
      
      // Auto-fill empty variations with original content if applicable
      const filledNewOption = fillEmptyVariationsWithOriginal(newOptionVariations);
      
      setTextOptions(prev => [...prev, filledNewOption]);
    } else if (editingOptionIndex !== null) {
      // Editing existing option - apply all changes
      setTextOptions(prev => {
        const updated = [...prev];
        const updatedOption = {
          ...updated[editingOptionIndex],
          ...finalChanges
        };
        
        // If we modified the original tab, check if we should auto-fill empty variations
        if (finalChanges.original !== undefined) {
          updated[editingOptionIndex] = fillEmptyVariationsWithOriginal(updatedOption);
        } else {
          updated[editingOptionIndex] = updatedOption;
        }
        
        return updated;
      });
    }

    // Reset state
    cancelEdit();
  };

  const cancelEdit = () => {
    setEditingOptionIndex(null);
    setIsAddingNew(false);
    setNewOptionText('');
    setTempChanges({} as Record<TabKey, string>); // Clear all temp changes on cancel
    setActiveTab('original');
    clearError();
  };

  const deleteOption = (index: number) => {
    setTextOptions(prev => prev.filter((_, i) => i !== index));
    if (editingOptionIndex === index) {
      cancelEdit();
    }
  };

  const loadVariationText = (variation: TabKey) => {
    // Save current changes to tempChanges before switching tabs
    if (newOptionText.trim() && (editingOptionIndex !== null || isAddingNew)) {
      setTempChanges(prev => ({
        ...prev,
        [activeTab]: newOptionText
      }));
    }

    // Load text for the new variation
    if (editingOptionIndex !== null) {
      // Check if we have temp changes for this variation first
      const tempText = tempChanges[variation];
      const originalText = textOptions[editingOptionIndex][variation] || '';
      setNewOptionText(tempText || originalText);
    } else if (isAddingNew) {
      // For new options, load from tempChanges if available
      const tempText = tempChanges[variation];
      setNewOptionText(tempText || '');
    } else {
      setNewOptionText('');
    }
    setActiveTab(variation);
  };

  const handleAutoGenerate = async () => {
    if (editingOptionIndex === null || activeTab === 'original') return;
    
    const option = textOptions[editingOptionIndex];
    // Use modified original text from tempChanges if available, otherwise use saved original
    const originalText = tempChanges.original || option.original;
    
    if (!originalText || originalText.trim() === '') {
      alert('Esta opción necesita tener texto en la pestaña "Original" para poder autogenerar variaciones.');
      return;
    }

    clearError();
    
    try {
      const variationType = activeTab as 'tuSingular' | 'tuPlural' | 'udSingular' | 'udPlural';
      const generatedText = await generateText(originalText, variationType);
      
      setNewOptionText(generatedText);
      
    } catch (error) {
      console.error('Error generating text:', error);
    }
  };

  return (
    <div className="mt-4 pt-2">
      <label className="form-label">Opciones de texto</label>
      <small className="text-muted d-block mb-3">
        Configura diferentes variaciones de las opciones de texto según el tipo de trato (tú/usted) y número (singular/plural).
      </small>
      
      {/* Options list - visible only when not editing */}
      {!isAddingNew && editingOptionIndex === null && (
        <div className="mb-4">
          {textOptions.length === 0 ? (
            <div className="text-center text-muted py-5 border rounded">
              <i className="bi bi-list-ul fs-1 d-block mb-2"></i>
              No hay opciones de texto agregadas
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {textOptions.map((option, index) => (
                <div 
                  key={index} 
                  className="border rounded p-3 bg-white shadow-sm"
                  style={{ 
                    borderColor: '#dee2e6',
                    transition: 'box-shadow 0.2s',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = ''}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1 me-3">
                      <div className="d-flex align-items-center mb-2">
                        <span className="badge bg-secondary me-2">Opción {index + 1}</span>
                      </div>
                      <div 
                        className="text-muted"
                        dangerouslySetInnerHTML={{ __html: option.original || '<em>Sin texto original</em>' }}
                        style={{ 
                          wordBreak: 'break-word',
                          fontSize: '0.95rem',
                          lineHeight: '1.5'
                        }}
                      />
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => startEditingOption(index)}
                        title="Editar"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => deleteOption(index)}
                        title="Eliminar"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add new button - visible when not editing and adding is allowed */}
      {!isAddingNew && editingOptionIndex === null && allowAddingOptions && (
        <div className="text-center">
          <button 
            type="button" 
            className="btn btn-outline-dark"
            onClick={startAddingOption}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Añadir nueva opción
          </button>
        </div>
      )}

      {/* Editor with tabs - visible when editing or adding */}
      {(isAddingNew || editingOptionIndex !== null) && (
        <div className="mt-4">
          <h6 className="mb-3">
            {isAddingNew ? 'Añadir nueva opción' : `Editar opción ${editingOptionIndex! + 1}`}
          </h6>
          
          {/* Tab Navigation */}
          <ul className="nav nav-tabs mb-0">
            {tabs.map(tab => (
              <li key={tab.key} className="nav-item">
                <button
                  className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => loadVariationText(tab.key)}
                  type="button"
                  style={{ color: '#000' }}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Editor for active tab */}
          <div 
            className="border border-top-0 p-3"
            style={{ 
              borderRadius: '0 0 0.375rem 0.375rem'
            }}
          >
            <div 
              className="border rounded"
              style={{ 
                border: '1px solid #ced4da',
                borderRadius: '0.375rem'
              }}
            >
              {/* Formatting toolbar */}
              <div className="d-flex align-items-center border-bottom bg-light">
                <div className="btn-group h-100" role="group" aria-label="Formato de texto">
                  <button
                    type="button"
                    className="btn btn-outline-secondary h-100 border-0"
                    onClick={() => applyFormatting('bold')}
                    title="Negrita"
                    style={{ borderRadius: 0 }}
                  >
                    <i className="bi bi-type-bold"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary h-100 border-0"
                    onClick={() => applyFormatting('italic')}
                    title="Cursiva"
                    style={{ borderRadius: 0 }}
                  >
                    <i className="bi bi-type-italic"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary h-100 border-0"
                    onClick={() => applyFormatting('underline')}
                    title="Subrayado"
                    style={{ borderRadius: 0 }}
                  >
                    <i className="bi bi-type-underline"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary h-100 border-0"
                    onClick={() => {
                      // Try to clean selected text first, if no selection clean all
                      const selection = window.getSelection();
                      if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
                        // Clean format of selected text only
                        document.execCommand('removeFormat', false);
                      } else {
                        // Clean all formatting
                        setNewOptionText(stripAllFormatting(newOptionText));
                      }
                    }}
                    title="Limpiar formato (selección o todo)"
                    style={{ borderRadius: 0 }}
                  >
                    <i className="bi bi-eraser"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary h-100 border-0"
                    onClick={() => applyFormatting('insertUnorderedList')}
                    title="Lista desordenada"
                    style={{ borderRadius: 0 }}
                  >
                    <i className="bi bi-list-ul"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary h-100 border-0"
                    onClick={() => applyFormatting('insertOrderedList')}
                    title="Lista ordenada"
                    style={{ borderRadius: 0 }}
                  >
                    <i className="bi bi-list-ol"></i>
                  </button>
                </div>
              </div>
              
              {/* Content Editor */}
              <ContentEditable
                html={newOptionText}
                disabled={false}
                onChange={(evt) => setNewOptionText(evt.target.value)}
                onPaste={handlePaste}
                style={{
                  padding: '1rem',
                  minHeight: '150px',
                  backgroundColor: '#fff',
                  border: 'none',
                  outline: 'none'
                }}
                className="w-100"
                placeholder={`Escribe el texto para ${tabs.find(t => t.key === activeTab)?.label}...`}
              />
            </div>

            {/* Auto-generate button */}
            {activeTab !== 'original' && !isAddingNew && editingOptionIndex !== null && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={handleAutoGenerate}
                  disabled={isGenerating || !textOptions[editingOptionIndex]?.original}
                  title={!textOptions[editingOptionIndex]?.original ? 'Ingresa primero el texto original' : 'Generar texto automáticamente basado en el original'}
                >
                  {isGenerating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Generando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-magic me-2"></i>
                      Autogenerar texto
                    </>
                  )}
                </button>
                {!textOptions[editingOptionIndex]?.original && (
                  <small className="text-warning ms-2">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Esta opción necesita texto original para autogenerar
                  </small>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={cancelEdit}
              >
                <i className="bi bi-x me-1"></i>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={saveOption}
                disabled={!newOptionText.trim()}
              >
                <i className="bi bi-check me-1"></i>
                Guardar
              </button>
            </div>

            {/* Error display */}
            {error && (
              <div className="alert alert-danger mt-3" role="alert">
                <i className="bi bi-exclamation-circle me-2"></i>
                {typeof error === 'string' ? error : 'Error al generar el texto'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editable Toggle - Moved to the end */}
      {showEditableToggle && (
        <div className="mt-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="textSelectableEditable"
              checked={isEditable}
              onChange={(e) => onEditableChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="textSelectableEditable" style={{ color: '#000' }}>
              Permitir edición por Arquitectos
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextSelectableEditor;